import { mapPriceField, type PriceInput } from '@/shared/utils/priceFormatter';
import type { ProductDetail } from '../../domain/models/ProductDetail';
import type { PdpResponseDTO } from '../dto/pdp.dto';
import { parseSizeList } from '@/features/customization/utils/parseSizeList';
import { normalizeNumericField } from '@/features/pdp/utils/normalizeNumericField';
import {
  normalizeStoneSpecifications,
  readOptionalString,
} from '@/features/pdp/utils/normalizeStoneSpecifications';

function normalizeGemstonesField(
  value: string | Array<{ count?: number; type?: string }> | null | undefined
): string | null {
  if (!value) return null;
  if (Array.isArray(value)) {
    return value.length > 0 ? JSON.stringify(value) : null;
  }
  return value;
}

function toDimensionValue(
  primary: number | string | null | undefined,
  fallback: number | string | null | undefined
): number | null {
  const primaryValue = normalizeNumericField(primary);
  if (primaryValue != null && primaryValue !== 0) return primaryValue;
  const fallbackValue = normalizeNumericField(fallback);
  if (fallbackValue != null && fallbackValue !== 0) return fallbackValue;
  return null;
}

function isRawPdpApiPayload(plain: Record<string, unknown>): boolean {
  return (
    'goldSellingPrice' in plain ||
    'gstPrice' in plain ||
    'diamondSellingPrice' in plain ||
    'coloredStoneSellingPrice' in plain ||
    'presetSolitaireSellingPrice' in plain
  );
}

function enrichSizeFlags(product: ProductDetail): ProductDetail {
  return {
    ...product,
    isSizeableProduct:
      product.isSizeableProduct ||
      parseSizeList(product.customizationOptions).length > 0 ||
      Boolean(product.size && product.size !== '00'),
    gmsFirstInstallmentDiscountPercentage:
      product.gmsFirstInstallmentDiscountPercentage ?? 0,
    gmsFirstInstallmentDiscountEnabled:
      product.gmsFirstInstallmentDiscountEnabled ?? false,
    additionalDiscountText: product.additionalDiscountText ?? null,
  };
}

function readSolitaireMetaFields(plain: Record<string, unknown>) {
  const idRaw = plain.solitaireId ?? plain.solitaire_id;
  return {
    solitaireType: readOptionalString(plain.solitaireType ?? plain.solitaire_type),
    solitaireId:
      idRaw != null && String(idRaw).trim() !== '' ? String(idRaw).trim() : null,
    solitaireShape: readOptionalString(plain.solitaireShape ?? plain.solitaire_shape),
    solitaireCutGrade: readOptionalString(plain.solitaireCutGrade ?? plain.solitaire_cut_grade),
    solitaireCutPolishSymmetry: readOptionalString(
      plain.solitaireCutPolishSymmetry ?? plain.solitaire_cut_polish_symmetry
    ),
    solitaireFluorescence: readOptionalString(
      plain.solitaireFluorescence ?? plain.solitaire_fluorescence
    ),
  };
}

function normalizeStoneFields(plain: Record<string, unknown>) {
  const gemstonesTypeAndCount = normalizeGemstonesField(
    (plain.gemstonesTypeAndCount ?? plain.gemstones) as
      | string
      | Array<{ count?: number; type?: string }>
      | null
      | undefined
  );

  return {
    diamondCarat: normalizeNumericField(plain.diamondCarat as number | string | null | undefined),
    diamondStoneType: (plain.diamondStoneType as string | null | undefined) ?? null,
    noOfDiamonds: normalizeNumericField(
      (plain.noOfDiamonds ?? plain.totalDiamonds) as number | string | null | undefined
    ),
    solitaireCarat: normalizeNumericField(
      (plain.solitaireCarat ?? plain.solitaireTotalCarat) as number | string | null | undefined
    ),
    solitaireClarity: (plain.solitaireClarity as string | null | undefined) ?? null,
    solitaireColor: (plain.solitaireColor as string | null | undefined) ?? null,
    solitaireTotalNoOfStones: normalizeNumericField(
      plain.solitaireTotalNoOfStones as number | string | null | undefined
    ),
    ...readSolitaireMetaFields(plain),
    gemstones: gemstonesTypeAndCount,
    gemstonesTypeAndCount,
    stoneSpecifications: normalizeStoneSpecifications(plain.stoneSpecifications),
  };
}

function normalizeMappedProductDetail(plain: Record<string, unknown>): ProductDetail {
  const base = plain as unknown as ProductDetail;

  return enrichSizeFlags({
    ...base,
    ...normalizeStoneFields(plain),
    canonical:
      (typeof plain.canonical === 'string' && plain.canonical) ||
      (typeof plain.canonicalUrl === 'string' && plain.canonicalUrl) ||
      base.canonical ||
      '',
    price: mapPriceField(plain.price as PriceInput),
    discountedPrice: mapPriceField(plain.discountedPrice as PriceInput),
    discountValue: mapPriceField(plain.discountValue as PriceInput),
    goldPrice: mapPriceField(plain.goldPrice as PriceInput, plain.goldSellingPrice as PriceInput),
    diamondPrice: mapPriceField(plain.diamondPrice as PriceInput, plain.diamondSellingPrice as PriceInput),
    diamondPriceAfterDiscount: mapPriceField(plain.diamondPriceAfterDiscount as PriceInput),
    gemstonePrice: mapPriceField(
      plain.gemstonePrice as PriceInput,
      plain.coloredStoneSellingPrice as PriceInput
    ),
    presetSolitairePrice: mapPriceField(
      plain.presetSolitairePrice as PriceInput,
      plain.presetSolitaireSellingPrice as PriceInput
    ),
    makingChargePrice: mapPriceField(plain.makingChargePrice as PriceInput),
    makingChargePriceAfterDiscount: mapPriceField(plain.makingChargePriceAfterDiscount as PriceInput),
    gst: mapPriceField(plain.gst as PriceInput, plain.gstPrice as PriceInput),
  });
}

/** Detect SSR/cache payloads that are missing core breakup rows. */
export function isPriceBreakupIncomplete(detail: ProductDetail): boolean {
  if (detail.hideBreakUp) return false;

  const hasMetalOrTax = detail.goldPrice != null || detail.gst != null;
  const hasOtherComponent =
    detail.diamondPrice != null ||
    detail.presetSolitairePrice != null ||
    detail.gemstonePrice != null ||
    detail.makingChargePrice != null;

  if (!hasMetalOrTax && (detail.price != null || hasOtherComponent)) {
    return true;
  }

  return false;
}

export function pdpDtoToProductDetail(dto: PdpResponseDTO): ProductDetail {
  const gemstonesTypeAndCount = normalizeGemstonesField(
    dto.gemstonesTypeAndCount ?? dto.gemstones
  );

  return {
    designId: dto.designId ?? 0,
    designName: dto.designName ?? '',
    designCode: dto.designCode ?? '',
    productCode: dto.productCode ?? null,
    skuCode: dto.skuCode ?? null,
    category: dto.category ?? dto.categoryType ?? '',
    itemCategory: dto.itemCategory ?? dto.itemCategoryName ?? '',
    collectionName: dto.collectionName ?? null,
    collectionPageUrl: dto.collectionPageUrl ?? null,

    price: mapPriceField(dto.price),
    discountedPrice: mapPriceField(dto.discountedPrice),
    discountValue: mapPriceField(dto.discountValue),
    discountPercent: dto.discountPercent ?? null,
    discountName: dto.discountName ?? null,
    flatDiscountPercent: dto.flatDiscountPercent ?? null,
    additionalDiscountText: dto.additionalDiscountText?.trim() || null,

    goldPrice: mapPriceField(dto.goldPrice, dto.goldSellingPrice),
    diamondPrice: mapPriceField(dto.diamondPrice, dto.diamondSellingPrice),
    diamondPriceAfterDiscount: mapPriceField(dto.diamondPriceAfterDiscount),
    discountOnDiamondPriceValue: dto.discountOnDiamondPriceValue ?? null,
    gemstonePrice: mapPriceField(dto.gemstonePrice, dto.coloredStoneSellingPrice),
    presetSolitairePrice: mapPriceField(dto.presetSolitairePrice, dto.presetSolitaireSellingPrice),
    makingChargePrice: mapPriceField(dto.makingChargePrice),
    makingChargePriceAfterDiscount: mapPriceField(dto.makingChargePriceAfterDiscount),
    makingChargeDiscountValue: dto.makingChargeDiscountValue ?? null,
    gst: mapPriceField(dto.gst, dto.gstPrice),

    metal: dto.metal ?? '',
    metalType: dto.metalType ?? '',
    goldPurity: dto.goldPurity ?? null,
    metalWeight: dto.metalWeight ?? 0,
    productWeight: dto.productWeight ?? null,
    diamondCarat: normalizeNumericField(dto.diamondCarat),
    diamondStoneType: dto.diamondStoneType ?? null,
    noOfDiamonds: normalizeNumericField(dto.noOfDiamonds ?? dto.totalDiamonds),
    solitaireCarat: normalizeNumericField(dto.solitaireCarat ?? dto.solitaireTotalCarat),
    solitaireClarity: dto.solitaireClarity ?? null,
    solitaireColor: dto.solitaireColor ?? null,
    solitaireTotalNoOfStones: normalizeNumericField(dto.solitaireTotalNoOfStones),
    ...readSolitaireMetaFields(dto as unknown as Record<string, unknown>),
    gemstones: gemstonesTypeAndCount,
    gemstonesTypeAndCount,
    stoneSpecifications: normalizeStoneSpecifications(dto.stoneSpecifications),

    productHeight: toDimensionValue(dto.productHeight, dto.height),
    productWidth: toDimensionValue(dto.productWidth, dto.width),
    productLength: toDimensionValue(dto.productLength, dto.length),

    mediaItems: dto.mediaItems ?? {},

    customizationId: dto.customizationId ?? 0,
    customizationOptions: dto.customizationOptions ?? null,
    availableCustomizations: dto.availableCustomizations ?? null,

    size: dto.size != null && dto.size !== '' ? String(dto.size) : null,
    isSizeableProduct:
      dto.isSizeableProduct === true ||
      parseSizeList(dto.customizationOptions).length > 0 ||
      Boolean(dto.size && dto.size !== '00'),
    nddAvailableSizes: dto.nddAvailableSizes ?? null,

    edd: dto.edd ?? null,
    eddFullDate: dto.eddFullDate ?? null,
    defaultDeliveryDate: dto.defaultDeliveryDate ?? '',
    warehouseDeliveryDate: dto.warehouseDeliveryDate ?? '',

    schema: dto.schema ?? null,
    tags: dto.tags ?? null,
    canonical: dto.canonical || dto.canonicalUrl || '',
    metaKeywords: dto.metaKeywords ?? '',
    metaDescription: dto.metaDescription ?? '',
    videoSchema: dto.videoSchema ?? '',
    imageSchema: dto.imageSchema ?? '',
    breadCrumbListSchema: dto.breadCrumbListSchema ?? '',

    shareUrl: dto.shareUrl ?? null,
    shortDesc: dto.shortDesc ?? null,
    disclaimer: dto.disclaimer ?? '',
    readyToShipQuantity: dto.readyToShipQuantity ?? null,
    hideBreakUp: dto.hideBreakUp ?? false,

    gmsFirstInstallmentDiscountPercentage: parseGmsDiscountPercentage(
      dto.gmsFirstInstallmentDiscountPercentage
    ),
    gmsFirstInstallmentDiscountEnabled: dto.gmsFirstInstallmentDiscountEnabled ?? false,
  };
}

function parseGmsDiscountPercentage(
  value: number | string | null | undefined
): number {
  if (value == null || value === '') return 0;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function productDetailToPlainObject(product: ProductDetail): Record<string, unknown> {
  return product as unknown as Record<string, unknown>;
}

export function plainObjectToProductDetail(plain: Record<string, unknown>): ProductDetail {
  if (isRawPdpApiPayload(plain)) {
    return pdpDtoToProductDetail(plain as PdpResponseDTO);
  }

  return normalizeMappedProductDetail(plain);
}
